import { ITracker as _EasyPostTracker } from "@easypost/api";
import { Currency } from "@redotech/money/currencies";
import { filterTruthy } from "@redotech/util/array";
import { convertArbitraryPhoneNumberToE164 } from "@redotech/util/phone-number";
import { getDropoffQrCodeUris } from "./dropoff/utils";
import { CustomerReturnCreatedAttachmentGenerationParameters } from "./email-builder";
import { Order, Provider } from "./order";
import {
  Channel,
  ExchangeItem,
  getCommercialInvoiceUrl,
  getReturner,
  MerchantAppReturn,
  PickupLocation,
  ProvisionType,
  Return,
  ReturnAddress,
  ReturnWarning,
  ReturnZod,
  ShipmentGroup,
} from "./return";
import { pickupLocationOptions } from "./return-flow";
import {
  getFieldsFromReturnTotalsSet,
  getProductFieldsFromReturnTotalsSet,
  ReturnTotalsSet,
} from "./returns/return-totals/return-totals-set";
import { SesEvent } from "./ses-events";
import { Tag } from "./tag";
import { TimelineEvent } from "./timeline";
import type { HttpsUrl } from "./url";
export enum TrackableType {
  ORDER = "order",
  RETURN = "return",
  FULFILLMENT_GROUP = "fulfillment_group",
}

import { TrackingPlatform } from "./trackable/tracking-platform";
export { TrackingPlatform } from "./trackable/tracking-platform";

/** A unique identifier for a shipment tracker */
interface TrackingPlatformIdentifier {
  /** The company providing tracking information (Optional for backwards compatibility) */
  trackingPlatform: TrackingPlatform;
  /** The identifier used by the tracking company */
  id: string;
}

/** Easypost tracker type with some fields removed and with the optional types fixed.*/
export interface _FixedEasyPostTracker {
  /**
   * The tracking code provided by the carrier
   */
  tracking_code: _EasyPostTracker["tracking_code"];

  /**
   * The current status of the package, possible values are "unknown", "pre_transit", "in_transit", "out_for_delivery", "delivered", "available_for_pickup", "return_to_sender", "failure", "cancelled" or "error"
   * "expired" is track123 specific
   */
  status: _EasyPostTracker["status"] | "expired";

  /**
   * Additional details about the current status, possible values are "unknown", "status_update", "departed_facility", "arrived_at_facility", "out_for_delivery", "arrived_at_destination"
   */
  status_detail: _EasyPostTracker["status_detail"];

  /**
   * The name of the carrier handling the shipment
   */
  carrier: _EasyPostTracker["carrier"];

  /**
   * Array of the associated TrackingDetail objects
   */
  tracking_details: (Omit<
    _EasyPostTracker["tracking_details"][number],
    | "source"
    | "object"
    | "tracking_location"
    | "status"
    | "description"
    | "status_detail"
    | "carrier_code"
  > & {
    tracking_location: Partial<
      Omit<
        _EasyPostTracker["tracking_details"][number]["tracking_location"],
        "object"
      >
    >;
    status: _EasyPostTracker["tracking_details"][number]["status"] | "expired";
    /** Provider-specific event ID for deduplication (e.g. WeGrow unique_id) */
    externalEventId?: string;
  })[];

  /**
   * URL to a publicly-accessible html page that shows tracking details for this tracker
   */
  public_url: _EasyPostTracker["public_url"];

  /**
   * The estimated delivery date provided by the carrier (if available)
   */
  est_delivery_date?: _EasyPostTracker["est_delivery_date"];

  /**
   * The id of the EasyPost Shipment object associated with the Tracker (if any)
   *
   * @remarks
   * Redo Returns uses this field to store reference the Return object.
   */
  shipment_id?: _EasyPostTracker["shipment_id"];

  /**
   * Carrier details including the address the package is being delivered to.
   * Inner fields are Partial because not all carriers populate every field
   * (same pattern as tracking_location above).
   */
  carrier_detail?: Partial<_EasyPostTracker["carrier_detail"]>;

  /**
   * The date and time the tracker was last updated (ISO 8601 format)
   */
  updated_at?: string;
}

/**
 * Representation of a shipment tracker event sent from a third-party tracking company.
 *
 * @remarks
 * We need an interface to fit multiple tracking providers (Easypost and Track123).
 * Heavily based on the Easypost Tracker object.
 */
export type ExternalShipmentTracker = TrackingPlatformIdentifier &
  _FixedEasyPostTracker;

const allShipmentStatuses_: Record<ExternalShipmentTracker["status"], null> = {
  pre_transit: null,
  unknown: null,
  in_transit: null,
  out_for_delivery: null,
  available_for_pickup: null,
  delivered: null,
  return_to_sender: null,
  failure: null,
  cancelled: null,
  error: null,
  expired: null,
};

export const allShipmentStatuses: ExternalShipmentTracker["status"][] =
  Object.keys(allShipmentStatuses_) as ExternalShipmentTracker["status"][];

/**
 * If you are trying to categorize an entire order as a single status even if it has
 * multiple shipments with different statuses, then use either the "lowest" or "highest"
 * status from this list, depending on your use case.
 * @param prioritizePreTransitOverUnknown If true, "pre_transit" is considered earlier
 * than "unknown". If false, "unknown" is considered earlier than "pre_transit". For
 * order tracking emails we prefer to display pre_transit instead of unknown, even though
 * unknown is technically earlier in the shipment lifecycle.
 *
 */
export const shipmentStatusChronologicalOrder: ({
  prioritizePreTransitOverUnknown,
}: {
  prioritizePreTransitOverUnknown: boolean;
}) => Record<ExternalShipmentTracker["status"], number> = ({
  prioritizePreTransitOverUnknown,
}) => ({
  pre_transit: prioritizePreTransitOverUnknown ? 0 : 1,
  unknown: prioritizePreTransitOverUnknown ? 1 : 0,
  in_transit: 2,
  out_for_delivery: 3,
  available_for_pickup: 4,
  delivered: 5,
  return_to_sender: 6,
  failure: 7,
  cancelled: 8,
  error: 9,
  expired: 10,
});

/**
 * Interface for a set of packages that can be tracked.
 * Essentially a stripped-down union of the Order and Return models,
 * molded into the ideal shape for order tracking use cases.
 *
 * @remarks
 * This interface is intended for use with the Order Tracking and Return Tracking products,
 * so that the emails and SMS messages can be generated agnostically.
 */
export interface BaseTrackable {
  /**
   * The Redo ID of the order or return or fulfillment group (MongoDB ObjectID)
   */
  id: string;
  customer: {
    shopifyId?: string;
    email: string;
    phoneNumber?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
  };
  fulfillments: TrackableFulfillment[];
  lineItems: TrackableLineItem[];
  currentEmailFlows: {
    emailFlowId: string;
    currentStep: number;
    continueDate: Date;
    fulfillmentId?: string;
  }[];

  /**
   * The time this object was created in Redo's system
   */
  createdAt: Date;

  /**
   * On orders, this is field is just a reference to itself.
   * On returns, it references the original order.
   */
  originalOrder: OriginalOrder;
  teamId: string;
  discount?: {
    id?: string | null;
    code?: string | null;
    description?: string | null;
    expirationDateTime?: string | null;
    status?: string | null;
  };
  trackingEmailsSent?: {
    emailId: string;
    status?: string;
    sentAt: string;
    s3URL?: string;
    trigger?: string;
    trackingCode?: string;
    attachmentInfo?: {
      hasAttachments?: boolean;
      attachmentCount?: number;
      attachmentNames?: string[];
    };
    manuallySent?: boolean;
  }[];
  trackingWebhooksSent?: {
    destinationUrl: string;
    sentAt: string;
    automationStepId: string;
    statusCode?: number;
    success: boolean;
    trigger?: string;
  }[];
  provider?: Provider;
}

export interface TrackableFulfillment {
  id: string;
  /**
   * Easypost trackers. This list may be empty.
   */
  trackers: ExternalShipmentTracker[];
  lineItems: TrackableFulfillmentLineItem[];
  isShipped: boolean;
  updatedAt?: Date;
  tracking_numbers?: string[];
  status: string;
  shipment_status?: string;
  tracking_company?: string;
  /** Carrier/source tracking URL (e.g. from Shopify fulfillment). Not the Redo tracking page. */
  tracking_url?: string;
  locationId?: string;
  /** Redo-hosted tracking page URL for this fulfillment. Populated in flow schema instances. */
  redoTrackingPageUrl?: HttpsUrl;
}

export interface TrackableFulfillmentLineItem extends TrackableLineItem {
  fulfilledQuantity?: number;
}

/**
 * Similar interfaces already exist for line items,
 * but for maximum control and speed, I'm making a new one (Josh)
 */
export interface TrackableLineItem {
  id: number | string;
  variantId?: string | null;
  productId?: string | null;
  title: string;
  variantTitle: string;
  quantity: number;
  /**
   * @deprecated Use priceSet instead
   */
  price: number;
  priceSet?: { presentment_money: { amount: number; currency_code: string } };
  totalDiscountSet?: {
    presentment_money: { amount: number; currency_code: string };
  };
  totalDiscount?: string;
  isRedo: boolean;
  requiresShipping: boolean;
  image: { src: string | undefined };
  tags: string[];
  properties: { [key: string]: string };
  sku: string;
  green_return: boolean;
  vendor?: string;
  handle?: string;
  compareAtPrice?: string | null;
  isTip?: boolean;
}

interface OriginalOrder {
  id: string;
  shopify: ShopifyOrder;
}

/**
 * Shopify tracking info. Not as useful as Easypost data but guaranteed to exist.
 */
export interface ShopifyFulfillment {
  id: string;
  updatedAt?: Date;
  shipmentStatus: string;
  isShipped: boolean;
  locationId?: string;
  tracking: {
    /*
     * Tracking numbers as entered in Shopify.
     * This list will always have at least one entry (although it may be null)
     * In rare cases, it may have more than one.
     */
    numbers: (string | null)[];
    /*
     * Tracking URLs as entered in Shopify.
     * This list will always have at least one entry (although it may be null)
     * In rare cases, it may have more than one.
     */
    urls: (string | null)[];
    company: string | null;
  };
}

/**
 * Normalizes the est_delivery_date field in an ExternalShipmentTracker.
 *
 * EasyPost has an issue where `est_delivery_date` is returned in UTC format,
 * but in most cases it's really just a date with 00:00 time, implying local time.
 * However, this isn't always true. Especially for FedEx, the date is often
 * genuinely the exact UTC timestamp when delivery is expected.
 *
 * The correct field to use is `carrier_detail.est_delivery_date_local`, which,
 * when it exists, represents the actual local delivery date without timezone conversion issues.
 *
 * This fix only applies to EasyPost trackers, as other tracking platforms may
 * handle dates differently.
 *
 * See {@link https://redo-tech.slack.com/archives/C09227VTHRB/p1761158604552649?thread_ts=1761068319.096039&cid=C09227VTHRB}
 */
function normalizeTrackerDeliveryDate(
  tracker: ExternalShipmentTracker,
): ExternalShipmentTracker {
  if (tracker.trackingPlatform !== TrackingPlatform.EASYPOST) {
    return tracker;
  }

  const localDeliveryDate = tracker.carrier_detail?.est_delivery_date_local;

  if (localDeliveryDate) {
    try {
      const localDate = new Date(localDeliveryDate);
      if (isNaN(localDate.getTime())) {
        return tracker;
      }
      return { ...tracker, est_delivery_date: localDate.toISOString() };
    } catch (error) {
      return tracker;
    }
  }

  return tracker;
}

/**
 * Similar interfaces already exist for shopify order data,
 * but for maximum control and speed, I'm making a new one (Josh)
 */
export interface ShopifyOrder {
  id: string;
  number?: number;
  name: string;
  createdAt: Date;
  contact_email: string;
  cancelReason?: string;
  /*
   * Intended for estimating delivery dates only, not general use.
   * Favor Trackable.lineItems instead, and avoid adding unnecessary fields here.
   */
  lineItems: { productId: string | null }[];
  fulfillments: ShopifyFulfillment[];
  customer: { id: string } | null;
  shippingLines: {
    title: string;
    price: number;
    discountedPrice: number;
    discountedPriceSet: { presentment_money: { amount: number } };
    carrierIdentifier: string;
    code: string;
  }[];
  priceBeforeDiscounts: number;
  totalDiscounts?: number;
  discounts: {
    individualDiscounts: {
      discountType: "fixed_amount" | "percentage";
      amount: number;
    }[];
  };
  presentmentCurrency: string;
  currentSubtotalPriceSet: { presentment_money: { amount: number } };
  currentTotalTaxSet: { presentment_money: { amount: number } };
  currentTotalPriceSet: { presentment_money: { amount: number } };
  shippingAddress?: {
    name: string;
    address1: string;
    address2: string;
    city: string;
    province: string;
    provinceCode: string;
    zip: string;
    country: string;
    countryCode: string;
    phone: string;
  };
  billingAddress?: {
    name: string;
    address1: string;
    address2: string;
    city: string;
    province: string;
    zip: string;
    country: string;
    countryCode: string;
    phone: string;
  };
  sourceName?: string;
  financialStatus?:
    | "authorized"
    | "paid"
    | "partially_paid"
    | "partially_refunded"
    | "pending"
    | "refunded"
    | "voided";
  note?: string;
  tags: string[];
  po_number?: string | null;
}

export type Trackable = BaseTrackable &
  (
    | { type: TrackableType.ORDER }
    | { type: TrackableType.FULFILLMENT_GROUP }
    | {
        type: TrackableType.RETURN;
        returnProducts: Return["products"] | ReturnZod["products"];
        status: Return["status"];
        shipment: Return["shipment"] | ReturnZod["shipment"];
        shipments: Return["shipments"] | ReturnZod["shipments"];
        reShipments: Return["reShipments"] | ReturnZod["reShipments"];
        customerReturnCreatedAttachmentGenerationParameters: CustomerReturnCreatedAttachmentGenerationParameters; // best name ever
        notes?: Return["notes"] | ReturnZod["notes"];
        notesToCustomerOnApproval?: string;
        notesToCustomerOnProcessing?: string;
        rejectReason?: string;
        merchantRejectionNotes?: string;
        returnFlowRejectionReasons?: string;
        pickup?: Return["pickup"] | ReturnZod["pickup"];
        returnType: Return["type"] | ReturnZod["type"];
        returnTypes: Return["returnType"] | ReturnZod["returnType"];
        markedForManualReview?: boolean;
        draftOrderURL?: string;
        warnings?: ReturnWarning[];
        inStoreReturn?: boolean;
        channel?: Channel;
        shipping_address?: ReturnAddress;
        merchant_address?: ReturnAddress;
        isManualLabel?: boolean;
        exchangeOrder: any[];
        /** Combined exchange items from advancedExchangeItems, exchange_for, and exchangeGroupItem */
        exchangeItems?: ExchangeItem[];
        form_label?: string;
        postage_label?: string;
        happyReturnsData?: any;
        blueYonder?: Return["blueYonder"];
        returnBear?: Return["returnBear"];
        dropoffs?: Return["dropoffs"];
        pickupDayOfWeek?: string;
        pickupDetail?: string;
        packingSlipLink?: string;
        repair?: Return["repair"];
        refundValue?: number;
        returnExpirationDate?: Date | null;
        provision?: ProvisionType;
        fedExEasyReturnsQrCodes?: string[] | null;
        returnTotalValue?: number;
        shop_currency?: Currency;
        tags?: Tag[];
        shipmentGroups?: ShipmentGroup[];
        /** Store credit amount (excluding original order shipping) */
        storeCredit?: number;
        /** Store credit amount including original order shipping */
        storeCreditWithShipping?: number;
        /** Total value of exchange/new order items */
        newOrderValue?: number;
        /** Amount customer owes for exchange upsell */
        outstandingBalance?: number;
        /** Return shipping fee paid by customer */
        shippingFee?: number;
        /** Shipping fee deducted from refund/store credit */
        shippingFeeDeducted?: number;
        /** Exchange shipping fee */
        exchangeShippingFee?: number;
        /** Exchange shipping method name */
        exchangeShippingName?: string;
        /** Processing/adjustment fee value */
        processingFee?: number;
        /** Loyalty points deducted */
        loyaltyPointsDeducted?: number;
        /** Loyalty points refunded */
        loyaltyPointsRefunded?: number;
        /** The primary ecommerce provider for this return (e.g., shopify, commentsold) */
        provider?: Provider;
        /** Whether customer notifications are enabled for this return */
        sendCustomerNotificationsForProcessing: boolean;
        /** Whether the latest processing trigger for this return was manual */
        isManuallyProcessingReturn: boolean;
      }
  );

export function orderToTrackable(order: Order): Trackable {
  const shopifyOrder = mapShopify(order);
  const lineItems =
    order.shopify.line_items?.map(orderLineItemToTrackableLineItem) ?? [];
  const phoneNumber =
    order.shopify.phone ||
    order.shopify?.customer?.phone ||
    order.shopify?.shipping_address?.phone;
  return {
    type: TrackableType.ORDER,
    id: order._id.toString(),
    customer: {
      shopifyId: order.shopify.customer?.id,
      email:
        // https://community.shopify.com/t/differences-between-email-and-contact-email-in-orders-api/3455
        // In most cases you’ll find the email and contact_email on an order being the same thing, even if someone is checking out as a guest they’ll still need to provide either an e-mail address or phone number depending on the shop’s settings.
        // The main difference between the two is that in the event an email isn’t provided on an order the email field won’t be populated, but contact_email will consider other information like a customer account’s email address so that the field doesn’t necessarily end up being blank.
        order.shopify.email ||
        order.shopify.contact_email ||
        order.shopify?.customer?.email,
      phoneNumber: phoneNumber
        ? convertArbitraryPhoneNumberToE164(phoneNumber) || undefined
        : undefined,
      name: order.customer_name,
      firstName: order.shopify.customer?.first_name,
      lastName: order.shopify.customer?.last_name,
    },
    fulfillments: order.shopify.fulfillments.map((fulfillment) => ({
      id: fulfillment.id?.toString(),
      trackers: order.trackers
        .filter((t) => {
          try {
            return t.fulfillmentID.toString() === fulfillment.id?.toString();
          } catch (e) {
            console.error(
              `Error comparing tracker fulfillment ID ${t.fulfillmentID} to fulfillment ID ${fulfillment.id}, order ID ${order._id}`,
            );
            return false;
          }
        })
        .map((t) => normalizeTrackerDeliveryDate(t._tracker)),
      // fulfillment line items have less info than root line items
      lineItems: fulfillment.line_items.map((lineItem) => {
        const trackableLineItem =
          lineItems.find((li) => li.id.toString() === lineItem.id.toString()) ??
          orderLineItemToTrackableLineItem(lineItem);
        return {
          ...trackableLineItem,
          // the root line item quantity is the total quantity of the item in the order
          // the fulfillment line item quantity is the quantity of the item that was actually fulfilled in this fulfillment
          fulfilledQuantity: lineItem.quantity,
        };
      }),
      isShipped: !!fulfillment.shipment_status,
      updatedAt: fulfillment.updated_at
        ? new Date(fulfillment.updated_at)
        : undefined,
      tracking_numbers: fulfillment.tracking_numbers,
      status: fulfillment.status,
      shipment_status: fulfillment.shipment_status ?? undefined,
      tracking_company: fulfillment.tracking_company ?? undefined,
      tracking_url: fulfillment.tracking_url ?? undefined,
      locationId: fulfillment.location_id,
    })),

    lineItems:
      order.shopify.line_items?.map(orderLineItemToTrackableLineItem) ?? [],
    createdAt: new Date(order.createdAt),
    originalOrder: { id: order._id.toString(), shopify: shopifyOrder },
    currentEmailFlows:
      order.currentEmailFlows?.map((f) => ({
        ...f,
        continueDate: new Date(f.continueDate),
      })) ?? [],
    teamId: order.team.toString(),
    discount: order.discount,
    trackingEmailsSent: order.trackingEmailsSent,
    trackingWebhooksSent: order.trackingWebhooksSent,
    provider: order.provider,
  };
}

function getReturnTotalsProductPrice(
  returnProductId: string,
  fallbackPrice: string,
  returnTotalsSet?: ReturnTotalsSet,
): string {
  const productTotals = returnTotalsSet
    ? getProductFieldsFromReturnTotalsSet({
        returnTotalsSet,
        productId: returnProductId,
      })
    : undefined;
  return productTotals?.originalPrice?.toString() ?? fallbackPrice;
}

export function returnToTrackable(
  return_: Return | MerchantAppReturn,
  order: Order,
  returnTotalsSet?: ReturnTotalsSet,
): Trackable {
  // returns don't track the same details about line items,
  // so we have to get the ones from the order object
  const returnProducts = (return_.products ?? []).map((product) => {
    // Find the corresponding ProductTotals to get the correct originalPrice
    return {
      ...product,
      price: getReturnTotalsProductPrice(
        product._id.toString(),
        product.price,
        returnTotalsSet,
      ),
      deliveredDate: product.deliveredDate
        ? new Date(product.deliveredDate)
        : undefined,
    };
  });
  const lineItems = (order.shopify.line_items ?? [])
    .filter((lineItem) =>
      return_.products.some(
        (product) => product.line_item_id.toString() === lineItem.id.toString(),
      ),
    )
    .map((lineItem) => {
      const product = return_.products.find(
        (product) => product.line_item_id.toString() === lineItem.id.toString(),
      );
      if (!product) {
        throw new Error("Product not found");
      }
      // Find the corresponding ProductTotals to get the correct originalPrice
      const originalPrice = getReturnTotalsProductPrice(
        product._id.toString(),
        lineItem.price,
        returnTotalsSet,
      );

      return orderLineItemToTrackableLineItem({
        ...lineItem,
        price: parseFloat(originalPrice),
      });
    });

  const shipments =
    (return_.shipments?.length || 0) > 0
      ? return_.shipments
      : return_.shipment
        ? [return_.shipment]
        : [];

  const returner = getReturner(return_);
  if (!returner.email) {
    throw new Error("No email found on returner");
  }

  const pickupDate = return_.pickup?.pickupDate
    ? Temporal.PlainDate.from(return_.pickup.pickupDate)
    : undefined;
  const pickupDayOfWeek = pickupDate?.toLocaleString(undefined, {
    weekday: "long",
  });

  const packagePickupLocation = return_.pickup?.pickupLocation?.packageLocation;
  const packagePickupSpecialInstructions =
    return_.pickup?.pickupLocation?.specialInstructions;

  const packagePickupDetail = return_.pickup
    ? packagePickupLocation === PickupLocation.OTHER
      ? `with the following instructions: "${packagePickupSpecialInstructions}"`
      : pickupLocationOptions().get(packagePickupLocation as PickupLocation)
          ?.detail
    : undefined;
  const latestProcessingTrigger =
    return_.processingTriggers && return_.processingTriggers.length > 0
      ? return_.processingTriggers[return_.processingTriggers.length - 1]
      : undefined;

  const customerReturnCreatedAttachmentGenerationParameters: CustomerReturnCreatedAttachmentGenerationParameters =
    {
      shipments:
        return_.shipments?.map((shipment) => ({
          postage_label: shipment.postage_label ?? undefined,
          form_label: shipment.form_label!,
          _shipment: shipment._shipment,
          customs_forms: shipment.customs_forms ?? undefined,
        })) ?? [],
      variables: {
        postage_label: return_.postage_label ?? undefined, // Return Shipping Label
        form_label: return_.form_label ?? undefined, // Return Shipping QR Code
        commercialInvoice: getCommercialInvoiceUrl(return_.shipment)?.url,
      },
      pickup_details: return_.shipment?.pickup,
      manualLabelData: return_.manualLabelData
        ? {
            shipments:
              return_.manualLabelData.shipments?.map((shipment) => ({
                packingSlipUrl: shipment.packingSlipUrl ?? undefined,
                returnItemIds: shipment.returnItemIds,
              })) ?? undefined,
          }
        : undefined,
      additionalShippingProviderDetails: {
        // Customer Return Shipping Provider Details
        labelUrl: return_.returnBear?.shippingInfo?.shippingLabelUrl,
      },
      dropoffQrCodeUrls: getDropoffQrCodeUris(return_),
    };

  // Combine exchange items from all sources:
  // 1. advancedExchangeItems - items from the advanced exchange cart
  // 2. exchange_for on products - variant exchange items
  // 3. exchangeGroupItem on products - exchange group items
  const exchangeForItems: ExchangeItem[] = (return_.products ?? [])
    .filter((p) => p.exchange_for)
    .map((p) => ({
      variantId: p.exchange_for!.variant_id,
      productId: p.exchange_for!.product_id,
      title: p.exchange_for!.product_title,
      variantTitle: p.exchange_for!.variant_title,
      images: p.exchange_for!.images,
      price: p.exchange_for!.price ?? undefined,
      quantity: p.quantity,
      sku: p.exchange_for!.sku ?? undefined,
    }));

  const exchangeGroupItems: ExchangeItem[] = (return_.products ?? [])
    .filter((p) => p.exchangeGroupItem)
    .map((p) => ({
      variantId: p.exchangeGroupItem!.variantId,
      productId: p.exchangeGroupItem!.productId,
      title: p.exchangeGroupItem!.title,
      variantTitle: p.exchangeGroupItem!.variantTitle,
      images: p.exchangeGroupItem!.imageSrc
        ? [p.exchangeGroupItem!.imageSrc]
        : [],
      price: p.exchangeGroupItem!.price ?? undefined,
      quantity: p.quantity,
    }));

  const advancedExchangeItems: ExchangeItem[] = (
    return_.advancedExchangeItems ?? []
  ).map((item) => ({
    variantId: item.variantId,
    productId: item.productId ?? undefined,
    title: item.title,
    variantTitle: item.variantTitle,
    images: item.images ? filterTruthy(item.images) : undefined,
    price: item.price ?? undefined,
    priceSet: item.priceSet ?? undefined,
    quantity: item.quantity,
    sku: item.sku ?? undefined,
  }));

  const exchangeItems: ExchangeItem[] = [
    ...exchangeForItems,
    ...exchangeGroupItems,
    ...advancedExchangeItems,
  ];

  const returnTotalsCalculations = returnTotalsSet
    ? getFieldsFromReturnTotalsSet({ returnTotalsSet })
    : undefined;

  return {
    returnProducts,
    type: TrackableType.RETURN,
    status: return_.status,
    returnType: return_.type,
    returnTypes: return_.returnType,
    exchangeOrder: return_.exchangeOrder ?? [],
    exchangeItems: exchangeItems.length > 0 ? exchangeItems : undefined,
    id: return_._id.toString(),
    customer: {
      email: returner.email!,
      name: returner.name,
      firstName: returner.firstName || "",
      lastName: returner.lastName || "",
      shopifyId: order.shopify.customer?.id,
      phoneNumber: returner.phone || undefined,
    },
    fulfillments: (shipments ?? []).map((shipment) => {
      // Get items for this specific shipment by matching shipmentGroupID
      const shipmentGroupId = shipment.shipmentGroupID;
      const shipmentLineItems = shipmentGroupId
        ? return_.products
            .filter((product) => {
              const productGroupIds = [
                ...(product.shipmentGroupIDs ?? []),
                ...(product.shipmentGroupID ? [product.shipmentGroupID] : []),
              ];

              return productGroupIds.includes(shipmentGroupId);
            })
            .map((product) =>
              lineItems.find(
                (li) => li.id.toString() === product.line_item_id.toString(),
              ),
            )
            .filter((li): li is TrackableLineItem => li != null)
        : [];

      return {
        id: shipment._shipment.id,
        trackers: shipment._shipment.tracker
          ? [normalizeTrackerDeliveryDate(shipment._shipment.tracker)]
          : [],
        // Use shipment-specific items if available, fallback to all items
        lineItems: shipmentLineItems.length > 0 ? shipmentLineItems : lineItems,
        isShipped: !["unknown", "pre_transit"].includes(
          shipment._shipment.status,
        ),
        updatedAt: shipment._shipment.updated_at
          ? new Date(shipment._shipment.updated_at)
          : undefined,
        status: shipment._shipment.status,
        shipment_status: shipment._shipment.status,
      };
    }),
    lineItems,
    createdAt: new Date(return_.createdAt),
    originalOrder: { id: order._id.toString(), shopify: mapShopify(order) },
    currentEmailFlows:
      return_.currentEmailFlows?.map((f) => ({
        ...f,
        continueDate: new Date(f.continueDate),
      })) ?? [],
    teamId: order.team.toString(),
    discount: return_.discount,
    shipment: return_.shipment,
    shipments: return_.shipments,
    reShipments: return_.reShipments,
    packingSlipLink: return_.packingSlipLink ?? undefined,
    customerReturnCreatedAttachmentGenerationParameters,
    notes: { notesToCustomer: return_.notes?.notesToCustomer ?? undefined },
    notesToCustomerOnApproval:
      return_.notes && "toCustomerOnApproval" in return_.notes
        ? return_.notes.toCustomerOnApproval
        : undefined,
    notesToCustomerOnProcessing:
      return_.notes && "toCustomerOnProcessing" in return_.notes
        ? return_.notes.toCustomerOnProcessing
        : undefined,
    rejectReason:
      return_.rejectReason ||
      return_.processingInput?.customerRejectionNotes ||
      "No reason provided",
    merchantRejectionNotes:
      return_.processingInput?.customerRejectionNotes ?? undefined,
    trackingEmailsSent:
      return_.trackingEmailsSent?.map((email) => ({
        ...email,
        sentAt:
          typeof email.sentAt === "string"
            ? email.sentAt
            : (email.sentAt?.toISOString() ?? ""),
        s3URL: email.s3URL ?? undefined,
      })) ?? undefined,
    trackingWebhooksSent:
      return_.trackingWebhooksSent?.map((webhook) => ({
        ...webhook,
        sentAt:
          typeof webhook.sentAt === "string"
            ? webhook.sentAt
            : webhook.sentAt.toISOString(),
      })) ?? undefined,
    pickup: return_.pickup,
    markedForManualReview: return_.markedForManualReview,
    draftOrderURL: return_.draftOrderURL,
    warnings: return_.warnings,
    inStoreReturn: return_.inStoreReturn || return_.isPosReturn || undefined,
    channel: return_.channel,
    isManualLabel: return_.isManualLabel ?? undefined,
    merchant_address: return_.merchant_address ?? undefined,
    shipping_address: return_.shipping_address ?? undefined,
    form_label: return_.form_label ?? undefined,
    postage_label: return_.postage_label ?? undefined,
    happyReturnsData: return_.happyReturnsData,
    provider: return_?.provider ?? order.provider ?? Provider.SHOPIFY,
    pickupDayOfWeek,
    pickupDetail: packagePickupDetail,
    repair: return_.repair
      ? {
          ...return_.repair,
          invoiceId: return_.repair.invoiceId ?? undefined,
          invoiceURL: return_.repair.invoiceURL ?? undefined,
          shopifyOrderId: return_.repair.shopifyOrderId ?? undefined,
        }
      : undefined,
    blueYonder: return_.blueYonder,
    returnBear: return_.returnBear,
    dropoffs: return_.dropoffs,
    refundValue: returnTotalsCalculations?.totalRefundWithoutShipping,
    returnExpirationDate: return_.expirationDate,
    provision: return_.provision,
    fedExEasyReturnsQrCodes: return_.blueYonder?.returns
      .map((ferRma) => ferRma.qrCodeUri)
      .filter((qrCodeUri) => qrCodeUri !== null && qrCodeUri !== undefined),
    returnTotalValue: returnTotalsCalculations?.totalReturnValue,
    shop_currency: order.shopify.currency,
    tags: return_.tags ?? [],
    shipmentGroups:
      return_.shipmentGroups?.map((group) => ({
        ...group,
        grams: group.grams ?? undefined,
        labelInstructions: group.labelInstructions ?? undefined,
      })) ?? [],
    // Additional return totals for cost summary
    storeCredit: returnTotalsCalculations?.totalStoreCredit,
    storeCreditWithShipping:
      returnTotalsCalculations?.totalStoreCreditWithShipping,
    newOrderValue: returnTotalsCalculations?.newOrderValue,
    outstandingBalance: returnTotalsCalculations?.displayCharge,
    shippingFee: returnTotalsCalculations?.shippingFee,
    exchangeShippingFee: returnTotalsCalculations?.exchangeShippingFee,
    exchangeShippingName: returnTotalsCalculations?.exchangeShippingName,
    processingFee: returnTotalsCalculations?.totalAdjustmentValue,
    loyaltyPointsDeducted: returnTotalsCalculations?.totalLoyaltyPointsDeducted,
    loyaltyPointsRefunded: returnTotalsCalculations?.totalLoyaltyPointsRefunded,
    sendCustomerNotificationsForProcessing:
      return_.processingInput?.sendNotifications ?? false,
    isManuallyProcessingReturn: latestProcessingTrigger?.type === "manual",
  };
}

export function mapShopify(order: Order): ShopifyOrder {
  const { shipping_address, billing_address, ...shopify } = order.shopify;
  const shippingAddress = shipping_address
    ? {
        name: shipping_address.name,
        address1: shipping_address.address1,
        address2: shipping_address.address2,
        city: shipping_address.city,
        province: shipping_address.province,
        provinceCode: shipping_address.province_code,
        zip: shipping_address.zip,
        country: shipping_address.country,
        countryCode: shipping_address.country_code,
        phone: shipping_address.phone,
      }
    : undefined;
  const billingAddress = billing_address
    ? {
        name: billing_address.name,
        address1: billing_address.address1,
        address2: billing_address.address2,
        city: billing_address.city,
        province: billing_address.province,
        provinceCode: billing_address.province_code,
        zip: billing_address.zip,
        country: billing_address.country,
        countryCode: billing_address.country_code,
        phone: billing_address.phone,
      }
    : undefined;
  return {
    id: shopify.id?.toString(),
    number: shopify.order_number,
    name: shopify.name,
    cancelReason: shopify.cancel_reason,
    contact_email: shopify.contact_email,
    createdAt: new Date(shopify.created_at),
    customer: shopify.customer ? { id: shopify.customer.id } : null,
    fulfillments: shopify.fulfillments.map((fulfillment) => ({
      id: fulfillment.id?.toString(),
      updatedAt: fulfillment.updated_at
        ? new Date(fulfillment.updated_at)
        : undefined,
      shipmentStatus: fulfillment.shipment_status || "unknown",
      isShipped: !["unknown", "pre_transit"].includes(
        fulfillment.shipment_status || "unknown",
      ),
      tracking: {
        numbers: fulfillment.tracking_numbers ?? [],
        urls: fulfillment.tracking_urls ?? [],
        company: fulfillment.tracking_company,
      },
      locationId: fulfillment.location_id,
    })),
    lineItems: shopify.line_items.map((line) => ({
      productId: line.product_id?.toString() ?? null,
    })),
    shippingLines:
      shopify.shipping_lines?.map((line: any) => ({
        title: line.title,
        price: parseFloat(line.price),
        discountedPrice: parseFloat(line.discounted_price),
        discountedPriceSet: line.discounted_price_set,
        carrierIdentifier: line.carrier_identifier,
        code: line.code,
      })) ?? [],
    presentmentCurrency: shopify.presentment_currency,
    priceBeforeDiscounts: parseFloat(shopify.total_line_items_price),
    totalDiscounts: parseFloat(shopify.total_discounts ?? "0"),
    currentSubtotalPriceSet: shopify.current_subtotal_price_set,
    discounts: {
      individualDiscounts: shopify.discount_applications?.map(
        ({ value, value_type }: any) => ({
          discountType: value_type,
          amount: parseFloat(value),
        }),
      ),
    },
    currentTotalTaxSet: shopify.current_total_tax_set || null,
    currentTotalPriceSet: shopify.current_total_price_set,
    shippingAddress,
    billingAddress,
    note: shopify.note,
    tags: shopify.tags?.split(",").map((tag: string) => tag.trim()) ?? [],
    sourceName: shopify.source_name,
    financialStatus: shopify.financial_status as
      | "authorized"
      | "paid"
      | "partially_paid"
      | "partially_refunded"
      | "pending"
      | "refunded"
      | "voided"
      | undefined,
    po_number: shopify.po_number,
  };
}

function orderLineItemToTrackableLineItem(
  lineItem: Order["shopify"]["line_items"][number],
): TrackableLineItem {
  const isTip = !!lineItem.tip;
  return {
    id: lineItem.id,
    title: isTip ? "Contribution" : lineItem.title,
    productId: lineItem.product_id?.toString() ?? undefined,
    variantId: lineItem.variant_id?.toString() ?? undefined,
    variantTitle: lineItem.variant_title,
    quantity: lineItem.current_quantity ?? lineItem.quantity,
    price: parseFloat(lineItem.price),
    priceSet: lineItem.price_set,
    totalDiscountSet: lineItem.total_discount_set,
    totalDiscount: lineItem.total_discount,
    isRedo: ["re:do", "redo"].includes(lineItem.vendor),
    requiresShipping: lineItem.requires_shipping,
    image: isTip
      ? { src: "https://assets.getredo.com/marketing/donation.png" }
      : lineItem.image?.src
        ? lineItem.image
        : {
            src: `https://placehold.co/150?text=${lineItem.title?.toLowerCase().includes("return") ? "Return" : (lineItem.title ?? "Product")}`,
          },
    tags: lineItem.tags ?? [],
    properties:
      lineItem.properties?.reduce(
        (acc, prop) => {
          acc[prop.name] = prop.value;
          return acc;
        },
        {} as Record<string, string>,
      ) ?? {},
    sku: lineItem.sku,
    green_return: lineItem.green_return,
    vendor: lineItem.vendor,
    isTip,
  };
}

/** Intended for use on Order and Return models, not general use */
export interface ITrackable {
  _id: string;
  timeline: TimelineEvent[];
  trackers: { _tracker: ExternalShipmentTracker; fulfillmentID: string }[];
  trackingTimeline: any[]; // TODO: define this type
  trackingAnalytics: {
    email: SesEvent[];
    page: {
      url: string;
      eventType: "ad" | "upsell" | "promotion" | "subscription";
      image?: string;
      createdAt: string;
    }[];
  };
  trackingTextsSent?: { sid: string; mms: boolean; sentAt: string }[];
  trackingEmailsSent?: {
    emailId: string;
    status?: string;
    sentAt: string;
    s3URL?: string;
    trigger?: string;
    trackingCode?: string;
    manuallySent?: boolean;
  }[];
  trackingWebhooksSent?: {
    destinationUrl: string;
    sentAt: string;
    automationStepId: string;
    statusCode?: number;
    success: boolean;
    trigger?: string;
  }[];
  trackingBillingStatus?: "billed" | "free";
  discount?: {
    id: string;
    description: string;
    code: string;
    expirationDateTime: string;
  };
}

export interface CostSummary {
  shippingProduct: string;
  shippingProductCost: string;
  taxCost: string;
  totalCost: string;
  priceBeforeDiscounts: string;
  discountStrings: string[];
}

/**
 * Return-specific cost summary for email templates.
 * Mirrors the structure displayed in the customer portal summary section.
 */
export interface ReturnCostSummary {
  /** Total value of items being returned (includes taxes + adjustments) */
  returnValue: string;
  /** Total value of exchange items (if any) */
  newOrderValue?: string;
  /** Return shipping fee (if customer pays) */
  returnShippingFee?: string;
  /** Exchange order shipping fee (if any) */
  exchangeShippingFee?: string;
  /** Exchange shipping method name */
  exchangeShippingName?: string;
  /** Amount customer owes for exchange upsell */
  outstandingBalance?: string;
  /** Store credit amount being issued */
  storeCredit?: string;
  /** Refund amount being issued */
  refundAmount?: string;
  /** Processing/convenience fee label (e.g., "Processing fee", "Convenience fee") */
  processingFeeLabel?: string;
  /** Processing/convenience fee amount */
  processingFee?: string;
  /** Whether payment is due immediately vs later */
  paymentDueImmediately?: boolean;
  /** Loyalty points deducted (if any) */
  loyaltyPointsDeducted?: number;
  /** Loyalty points refunded (if any) */
  loyaltyPointsRefunded?: number;
}

export interface SavingsSummary {
  fullPrice: string;
  totalSavings: string;
}

// Record type ensures exhaustiveness
const trackingStatusHelper: Record<
  ExternalShipmentTracker["status"],
  undefined
> = {
  unknown: undefined,
  pre_transit: undefined,
  in_transit: undefined,
  out_for_delivery: undefined,
  available_for_pickup: undefined,
  cancelled: undefined,
  delivered: undefined,
  error: undefined,
  failure: undefined,
  return_to_sender: undefined,
  expired: undefined,
} as const;

export const trackingStatuses = Object.keys(
  trackingStatusHelper,
) as (keyof typeof trackingStatusHelper)[];
